import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import classNames from "classnames";

// @ts-ignore
import logo from "../assets/icon/observatory_ios.128.png";
// @ts-ignore
import vnLogo from "../assets/icon/vn_observatory_ios.128.png";

function MenuItems({
  isVn,
  isProductLanding,
  onNavigate,
}: {
  isVn: boolean;
  isProductLanding: boolean;
  onNavigate?: () => void;
}) {
  const githubUrl = isVn
    ? "https://github.com/benjamindean/visual-novel-observatory"
    : "https://github.com/benjamindean/observatory";

  return (
    <>
      <li className="lg:pr-2">
        <a
          href={githubUrl}
          target="_blank"
          rel="noreferrer"
          onClick={onNavigate}
          className={
            isProductLanding
              ? "text-slate-600 hover:bg-slate-200/70 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-white/[0.06] dark:hover:text-white"
              : undefined
          }
        >
          GitHub
          <span className="sr-only">(opens in a new tab)</span>
        </a>
      </li>
      <li className="lg:pr-2">
        <Link
          to="/privacy-policy"
          onClick={onNavigate}
          className={
            isProductLanding
              ? "text-slate-600 hover:bg-slate-200/70 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-white/[0.06] dark:hover:text-white"
              : undefined
          }
        >
          Privacy Policy
        </Link>
      </li>
      <li className="lg:pr-2">
        <Link
          to="/terms-and-conditions"
          onClick={onNavigate}
          className={
            isProductLanding
              ? "text-slate-600 hover:bg-slate-200/70 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-white/[0.06] dark:hover:text-white"
              : undefined
          }
        >
          Terms &amp; Conditions
        </Link>
      </li>
    </>
  );
}

export default function NavBar({
  theme,
  onToggleTheme,
}: {
  theme: "light" | "dark";
  onToggleTheme: () => void;
}) {
  const location = useLocation();
  const isVn = location.pathname.replace(/\/$/, "") === "/visual-novels";
  const isProductLanding = isVn || location.pathname === "/";
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  function closeMenu({ restoreFocus = false }: { restoreFocus?: boolean } = {}) {
    setIsMenuOpen(false);

    if (restoreFocus) {
      menuButtonRef.current?.focus();
    }
  }

  return (
    <header
      className={classNames("w-full border-b transition-colors duration-300", {
        "border-slate-200 bg-[#F7F8FB] text-slate-950 dark:border-white/[0.08] dark:bg-[#070A12] dark:text-slate-100":
          isProductLanding,
        "bg-base-100 border-base-200 text-base-content": !isProductLanding,
      })}
    >
      <nav
        aria-label="Primary navigation"
        className="relative mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6"
      >
        <div className="flex min-w-0 items-center gap-2">
          <button
            ref={menuButtonRef}
            type="button"
            aria-label={isMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-navigation"
            className="btn btn-ghost min-h-11 min-w-11 px-2 lg:hidden"
            onClick={() => setIsMenuOpen((open) => !open)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                closeMenu({ restoreFocus: true });
              }
            }}
          >
            <svg
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d={isMenuOpen ? "M6 18 18 6M6 6l12 12" : "M4 6h16M4 12h8m-8 6h16"}
              />
            </svg>
          </button>

          {isMenuOpen && (
            <ul
              id="mobile-navigation"
              className={classNames(
                "menu menu-sm absolute left-4 top-[calc(100%+0.5rem)] z-50 w-56 rounded-box border p-2 shadow-xl lg:hidden",
                {
                  "border-slate-200 bg-white text-slate-950 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100":
                    isProductLanding,
                  "bg-base-100 border-base-200 text-base-content": !isProductLanding,
                },
              )}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  closeMenu({ restoreFocus: true });
                }
              }}
            >
              <MenuItems
                isVn={isVn}
                isProductLanding={isProductLanding}
                onNavigate={() => closeMenu()}
              />
            </ul>
          )}

          <Link className="flex min-w-0 items-center" to={isVn ? "/visual-novels" : "/"}>
            <span className="h-10 w-10 shrink-0 overflow-hidden rounded-full">
              <img
                src={isVn ? vnLogo : logo}
                alt=""
                width="128"
                height="128"
                className="h-full w-full object-cover"
                decoding="async"
              />
            </span>
            <span className="ml-2 min-w-0 truncate text-base font-medium leading-tight sm:text-xl">
              {isVn ? "Visual Novel Observatory" : "Observatory"}
            </span>
          </Link>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <ul className="menu menu-horizontal hidden px-1 lg:flex">
            <MenuItems isVn={isVn} isProductLanding={isProductLanding} />
          </ul>
          <button
            type="button"
            aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
            title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
            onClick={onToggleTheme}
            className={classNames("btn btn-ghost min-h-11 min-w-11 rounded-full p-0", {
              "text-slate-600 hover:bg-slate-200/70 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-white/[0.06] dark:hover:text-white":
                isProductLanding,
            })}
          >
            {theme === "dark" ? (
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                className="h-5 w-5"
              >
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.66 6.34l1.41-1.41" />
              </svg>
            ) : (
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                className="h-5 w-5"
              >
                <path d="M20.4 14.5A8.5 8.5 0 0 1 9.5 3.6 8.5 8.5 0 1 0 20.4 14.5Z" />
              </svg>
            )}
          </button>
        </div>
      </nav>
    </header>
  );
}
